import CodeSnippet from './CodeSnippet';

const quickstart = `import { RcrtClient, staticTokenProvider, CardsModule } from '@possibl/rcrt-sdk';

const rcrt = new RcrtClient({
  apiUrl: RCRT_API,
  tokenProvider: staticTokenProvider(WORKSPACE_KEY), // tk_*
});

// chat with an agent, stream the reply
const { session_id } = await rcrt.chat.send({
  message: 'Summarise my inbox',
  target_agent: 'crm-advisor',
});

rcrt.chat.sessionStream(session_id, {
  onDelta: ({ delta }) => ui.append(delta),
  onMessage: (bc) => {
    if (bc.tags.includes('interpret:pending-action')) {
      ui.card(CardsModule.extractCard(bc)); // human approves
    }
  },
});`;

const fanout = `// a console over dozens of workspaces,
// each client permanently bound to its tenant
const reports = await Promise.all(
  workspaces.map((ws) =>
    rcrt.forTenant(ws.tenantId)
      .marketplace.listInstalledBundles(),
  ),
);`;

const MODULES = [
  'breadcrumbs',
  'chat + SSE',
  'cards',
  'files',
  'marketplace',
  'org',
  'grants',
  'members',
  'forTenant()',
];

/**
 * 07 · Build on it — the SDK. This very page runs on it: the calculator's
 * JIT-UI advisor streams through @possibl/rcrt-sdk.
 */
export default function BuildWithSdk() {
  return (
    <section id="sdk" className="tint">
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-num">07 · Build on it</div>
          <div>
            <h2>
              One typed SDK. <span className="ph">This page runs on it.</span>
            </h2>
            <p className="lede">
              @possibl/rcrt-sdk is the developer surface: isomorphic TypeScript for browser,
              Node 18+ and React Native. One client for agents, streaming, approvals, files
              and multi-workspace fan-out. Bring your own identity provider — the SDK never
              dictates one. The live advisor in the calculator above is built with it.
            </p>
          </div>
        </div>

        <div className="sdk-install">
          <span className="sdk-cmd">
            <span className="ph">$</span> npm install @possibl/rcrt-sdk
          </span>
          <span className="sdk-ver">v0.5.1 · MIT · pin exact versions</span>
        </div>

        <div className="sdk-grid">
          <div className="sdk-col">
            <h3>Agents, streaming, approvals</h3>
            <p className="sub">
              Everything is a breadcrumb — chat, memory, pending actions. One CRUD surface
              underneath it all; actions gate on a human tap.
            </p>
            <CodeSnippet title="quickstart.ts" code={quickstart} />
          </div>
          <div className="sdk-col">
            <h3>Multi-tenant by default</h3>
            <p className="sub">
              Platform → org → workspace isolation enforced at the row level. Fan out across
              a fleet without ever mutating one client's tenant.
            </p>
            <CodeSnippet title="fleet-fanout.ts" code={fanout} />
            <div className="sdk-mods">
              {MODULES.map((m) => (
                <span key={m} className="sdk-mod">
                  {m}
                </span>
              ))}
            </div>
            <p className="ai-note">
              Also on the runtime: an MCP server exposing your workspace tools to Claude
              Code and other MCP clients, and cross-org agent federation with @org
              addressing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
